"use client";

import { useSession } from "next-auth/react";
import { useQuery, useMutation, useQueryClient, keepPreviousData } from "@tanstack/react-query";

function getBaseUrl(): string {
  return (
    process.env.NEXT_PUBLIC_API_CLOUDFRONT ||
    process.env.NEXT_PUBLIC_BACKEND_URL ||
    "https://api.divineinfinitebeing.com/"
  );
}

async function apiPostClient<T>(
  endpoint: string,
  body: unknown,
  token?: string
): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`${getBaseUrl()}${endpoint}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(body),
    });
  } catch {
    throw new Error("Network error — please check your connection");
  }

  if (!res.ok) {
    // Prefer the backend's own error message when available
    let message = `API error ${res.status}`;
    try {
      const errData = await res.clone().json() as { message?: string };
      if (errData.message) message = errData.message;
    } catch { /* body is not JSON — keep the default status-code message */ }
    throw new Error(message);
  }

  return res.json() as Promise<T>;
}

export function useApiList<T>(
  endpoint: string,
  params: {
    page?: number;
    limit?: number;
    sortField?: string;
    sortType?: "asc" | "desc";
    condition?: Record<string, unknown>;
    searchcondition?: Record<string, unknown>;
    /**
     * Optional separate endpoint to fetch the total count (e.g. "user/user-list-count").
     * When provided, two parallel API calls are made: one for paginated data, one for the count.
     * The count call always forwards active filters (condition + searchcondition) so the
     * returned count reflects the filtered result set, not the full collection.
     */
    countEndpoint?: string;
    /**
     * When true, uses the v2 payload format:
     *   - `limit` and `skip` nested inside the `condition` object
     *   - Extra mandatory fields: token, static_id, project, count
     *
     * When false (default), uses the legacy format:
     *   - `skip` and `limit` at the root of the payload
     *
     * Only set this to true for endpoints that have migrated to the v2 API contract.
     * All other callers remain on the legacy format automatically.
     */
    nestedPagination?: boolean;
  } = {},
  options?: { enabled?: boolean }
) {
  const { data: session } = useSession();
  const {
    page = 1,
    limit = 10,
    sortField = "_id",       // safe default for all endpoints; callers override per-table
    sortType = "desc",
    condition = {},
    searchcondition = {},
    countEndpoint,
    nestedPagination = false, // default false — preserves backward compat for all callers
  } = params;

  return useQuery({
    queryKey: [endpoint, page, limit, sortField, sortType, condition, searchcondition, countEndpoint, nestedPagination],
    // Keep previous page data visible while the next page loads — prevents table flash
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const skip = (page - 1) * limit;

      // ── Build the data request payload ──────────────────────────────────────
      const payload = nestedPagination
        ? {
            condition: { ...condition, limit, skip },
            searchcondition,
            sort: { type: sortType, field: sortField },
            project: {},
            token: "",
          }
        : {
            condition,
            sort: { type: sortType, field: sortField },
            skip,
            limit,
            searchcondition,
          };

      // ── Dual-API path ────────────────────────────────────────────────────────
      if (countEndpoint) {
        // Count endpoint receives filters. For nested pagination, it also expects
        // limit/skip and sort (as per user example).
        const countPayload = nestedPagination
          ? { 
              condition: { ...condition, limit: 5, skip: 0 }, 
              searchcondition, 
              sort: { type: sortType, field: sortField },
              project: {}, 
              token: "" 
            }
          : { condition: { ...condition }, searchcondition, count: true };

        const [dataRes, countRes] = await Promise.all([
          apiPostClient<{ status: string; results: { res: T[] }; count?: number; dt?: { n?: number } }>(
            endpoint,
            payload,
            session?.token
          ),
          apiPostClient<{ status: string; count?: number; dt?: { n?: number } }>(
            countEndpoint,
            countPayload,
            session?.token
          ),
        ]);

        const total = countRes.count ?? countRes.dt?.n ?? 0;
        return { data: dataRes.results?.res ?? [], count: total };
      }

      // ── Single-API path ──────────────────────────────────────────────────────
      const res = await apiPostClient<{
        status: string;
        results: { res: T[] };
        count?: number;
        dt?: { n?: number };
      }>(endpoint, payload, session?.token);

      return { data: res.results?.res ?? [], count: res.count ?? res.dt?.n ?? 0 };
    },
    enabled: !!session?.token && (options?.enabled !== false),
  });
}

export function useApiPost<TData = unknown, TBody = unknown>(
  endpoint: string,
  options?: {
    /**
     * Query keys to selectively invalidate after a successful mutation.
     * Pass the list endpoint key(s) that should re-fetch (e.g. ["user/user-list"]).
     * When omitted, no automatic cache invalidation occurs.
     */
    invalidateKeys?: string[];
  }
) {
  const { data: session } = useSession();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: TBody) => apiPostClient<TData>(endpoint, body, session?.token),
    onSuccess: () => {
      if (options?.invalidateKeys?.length) {
        for (const key of options.invalidateKeys) {
          void queryClient.invalidateQueries({ queryKey: [key] });
        }
      }
    },
  });
}

export function useApiGet<T>(endpoint: string, options?: { enabled?: boolean }) {
  const { data: session } = useSession();

  return useQuery({
    queryKey: [endpoint],
    queryFn: async () => {
      const res = await fetch(`${getBaseUrl()}${endpoint}`, {
        headers: {
          "Content-Type": "application/json",
          ...(session?.token ? { Authorization: `Bearer ${session.token}` } : {}),
        },
      });
      if (!res.ok) throw new Error(`API error ${res.status}`);
      return res.json() as Promise<T>;
    },
    enabled: !!session?.token && (options?.enabled !== false),
  });
}
